import { CheckCircle2, MessageCircle } from 'lucide-react'
import { Button } from '@/components/ui/button'
import React from 'react'
import { BlogPost } from '@/types/blog'
import { postFutebol2Anos } from './posts/futebol-2-anos'
import { postBeneficios } from './posts/beneficios-desenvolvimento'
import { postAulaDomicilio } from './posts/aula-domicilio'
import { postFutebolTimidez } from './posts/futebol-timidez'
import { postCriancaEnergia } from './posts/crianca-energia'
import { postImportancia2Anos } from './posts/importancia-2-anos'

export type { BlogPost }

const whatsappUrlMoema =
  'https://wa.me/5511963087549?text=Ol%C3%A1%2C%20li%20o%20artigo%20sobre%20Moema%20e%20gostaria%20de%20mais%20informa%C3%A7%C3%B5es%20sobre%20as%20aulas%20do%20SuperFut%20Kids%21'
const whatsappUrlVilaOlimpia =
  'https://wa.me/5511963087549?text=Ol%C3%A1%2C%20li%20o%20artigo%20sobre%20Vila%20Ol%C3%ADmpia%20e%20gostaria%20de%20mais%20informa%C3%A7%C3%B5es%20sobre%20as%20aulas%20do%20SuperFut%20Kids%21'
const whatsappUrlItaim =
  'https://wa.me/5511963087549?text=Ol%C3%A1%2C%20li%20o%20artigo%20sobre%20Itaim%20Bibi%20e%20gostaria%20de%20mais%20informa%C3%A7%C3%B5es%20sobre%20as%20aulas%20do%20SuperFut%20Kids%21'
const whatsappUrlPinheiros =
  'https://wa.me/5511963087549?text=Ol%C3%A1%2C%20li%20o%20artigo%20sobre%20Pinheiros%20e%20gostaria%20de%20mais%20informa%C3%A7%C3%B5es%20sobre%20as%20aulas%20do%20SuperFut%20Kids%21'
const whatsappUrlAltoPinheiros =
  'https://wa.me/5511963087549?text=Ol%C3%A1%2C%20li%20o%20artigo%20sobre%20Alto%20de%20Pinheiros%20e%20gostaria%20de%20mais%20informa%C3%A7%C3%B5es%20sobre%20as%20aulas%20do%20SuperFut%20Kids%21'

export const blogPosts: BlogPost[] = [
  postImportancia2Anos,
  {
    slug: 'aula-futebol-infantil-moema',
    title: 'Aula de Futebol Infantil em Moema - A Domicílio',
    excerpt:
      'Aulas de futebol a domicílio para crianças de 2 a 7 anos em Moema, São Paulo. Metodologia lúdica, professores especializados. Agende uma aula experimental!',
    date: '16 Fev, 2026',
    author: 'Equipe SuperFut',
    category: 'Local',
    location: 'Moema, SP',
    image:
      'https://img.usecurling.com/p/800/400?q=kids%20playing%20soccer%20park',
    metaDescription:
      'Aulas de futebol a domicílio para crianças de 2 a 7 anos em Moema, São Paulo. Metodologia lúdica, professores especializados. Agende uma aula experimental!',
    faq: [
      {
        question: 'Qual idade mínima?',
        answer:
          'Atendemos crianças a partir de 1 ano e meio (18 meses) até os 7 anos de idade.',
      },
      {
        question: 'Preciso ter espaço grande?',
        answer:
          'Não! Adaptamos as atividades para o espaço disponível, seja no play do prédio, quadra, jardim ou até mesmo na sala de estar (para os menores).',
      },
      {
        question: 'Meu filho nunca jogou, tem problema?',
        answer:
          'Nenhum problema. Nossa metodologia respeita o ritmo de cada criança, começando do zero e evoluindo a coordenação motora de forma divertida.',
      },
      {
        question: 'Atendem finais de semana?',
        answer:
          'Sim, temos disponibilidade de horários aos sábados e domingos, além dos dias de semana.',
      },
      {
        question: 'Quanto custa?',
        answer:
          'Os valores variam conforme a frequência (1x ou 2x na semana) e o formato (individual ou grupo). Entre em contato no WhatsApp para receber a tabela atualizada!',
      },
    ],
    content: (
      <div className="space-y-8">
        <div className="prose prose-slate max-w-none text-slate-700">
          <p className="lead text-lg md:text-xl">
            Se você mora em <strong>Moema</strong> e procura uma atividade
            física que una diversão, desenvolvimento e comodidade para o seu
            filho, o <strong>SuperFut Kids</strong> é a escolha ideal.
          </p>
          <p>
            Sabemos que a rotina dos pais em São Paulo é corrida. Levar as
            crianças para escolas de esportes no trânsito nem sempre é viável.
            Por isso, levamos o futebol até você! Nossas aulas acontecem no seu
            condomínio, parque mais próximo (como o Ibirapuera ou o Parque das
            Bicicletas) ou onde for melhor para a sua família.
          </p>
        </div>

        <section>
          <h2 className="text-2xl md:text-3xl font-display font-bold text-super-text mb-4">
            Por que escolher o SuperFut Kids em Moema?
          </h2>
          <ul className="grid gap-3 sm:grid-cols-2">
            {[
              'Professores que vão até o seu condomínio',
              'Material esportivo incluso e higienizado',
              'Horários flexíveis (manhã, tarde e finais de semana)',
              'Segurança e conforto para a família',
            ].map((item, i) => (
              <li key={i} className="flex items-start gap-2">
                <CheckCircle2 className="w-5 h-5 text-super-green mt-1 flex-shrink-0" />
                <span className="font-medium text-slate-700">{item}</span>
              </li>
            ))}
          </ul>
        </section>

        <section className="bg-slate-50 p-6 rounded-2xl border border-slate-100">
          <h2 className="text-2xl md:text-3xl font-display font-bold text-super-text mb-6">
            Especialistas em cada fase
          </h2>
          <div className="space-y-6">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="w-16 h-16 bg-super-yellow rounded-full flex items-center justify-center font-bold text-super-text text-xl flex-shrink-0">
                2-3
              </div>
              <div>
                <h3 className="text-xl font-bold text-super-text mb-2">
                  Primeiros Passos (2 a 3 anos)
                </h3>
                <p className="text-slate-600">
                  Foco na descoberta! Atividades sensoriais, reconhecimento do
                  corpo e contato inicial com a bola. Tudo muito lúdico, com
                  músicas e histórias.
                </p>
              </div>
            </div>
            <div className="flex flex-col md:flex-row gap-4">
              <div className="w-16 h-16 bg-super-green rounded-full flex items-center justify-center font-bold text-white text-xl flex-shrink-0">
                4-5
              </div>
              <div>
                <h3 className="text-xl font-bold text-super-text mb-2">
                  Craques da Bola (4 a 5 anos)
                </h3>
                <p className="text-slate-600">
                  Introdução aos fundamentos: chute, passe e controle. Começamos
                  a trabalhar pequenas regras e jogos cooperativos.
                </p>
              </div>
            </div>
            <div className="flex flex-col md:flex-row gap-4">
              <div className="w-16 h-16 bg-super-blue rounded-full flex items-center justify-center font-bold text-white text-xl flex-shrink-0">
                6-7
              </div>
              <div>
                <h3 className="text-xl font-bold text-super-text mb-2">
                  Campeões (6 a 7 anos)
                </h3>
                <p className="text-slate-600">
                  Refinamento técnico e jogos mais estruturados. Trabalhamos a
                  tomada de decisão, espírito de equipe e desafios técnicos.
                </p>
              </div>
            </div>
          </div>
        </section>

        <section>
          <h2 className="text-2xl md:text-3xl font-display font-bold text-super-text mb-4">
            Benefícios do futebol para crianças de 2 a 7 anos
          </h2>
          <p className="text-slate-600 mb-6">
            Mais do que formar jogadores, formamos crianças saudáveis e felizes.
            A prática regular com o SuperFut proporciona:
          </p>
          <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-4">
            {[
              'Coordenação motora',
              'Desenvolvimento cognitivo',
              'Habilidades sociais',
              'Saúde física',
              'Autoconfiança',
              'Rotina ativa',
            ].map((benefit, i) => (
              <div
                key={i}
                className="bg-white shadow-sm border border-slate-100 p-4 rounded-xl text-center font-bold text-slate-700 hover:border-super-blue transition-colors"
              >
                {benefit}
              </div>
            ))}
          </div>
        </section>

        <section className="bg-gradient-to-br from-green-50 to-blue-50 p-8 rounded-3xl text-center">
          <h2 className="text-2xl md:text-3xl font-display font-bold text-super-text mb-4">
            Agende uma aula experimental em Moema!
          </h2>
          <p className="text-lg text-slate-700 mb-8 max-w-2xl mx-auto">
            Vamos juntos transformar a energia do seu filho em desenvolvimento?
            Chame no WhatsApp e verifique a disponibilidade para o seu endereço.
          </p>
          <Button
            size="lg"
            className="bg-[#25D366] hover:bg-[#128C7E] text-white font-bold text-lg px-8 rounded-full shadow-lg hover:scale-105 transition-all w-full sm:w-auto"
            asChild
          >
            <a
              href={whatsappUrlMoema}
              target="_blank"
              rel="noopener noreferrer"
            >
              <MessageCircle className="w-6 h-6 mr-2" />
              Falar no WhatsApp
            </a>
          </Button>
        </section>
      </div>
    ),
  },
  {
    slug: 'aula-futebol-infantil-vila-olimpia',
    title: 'Aula de Futebol Infantil na Vila Olímpia - A Domicílio',
    excerpt:
      'Aulas de futebol a domicílio para crianças de 2 a 7 anos na Vila Olímpia, São Paulo. Metodologia lúdica, professores especializados. Agende uma aula experimental!',
    date: '16 Fev, 2026',
    author: 'Equipe SuperFut',
    category: 'Local',
    location: 'Vila Olímpia, SP',
    image:
      'https://img.usecurling.com/p/800/400?q=kids%20playing%20soccer%20urban',
    metaDescription:
      'Aulas de futebol a domicílio para crianças de 2 a 7 anos na Vila Olímpia, São Paulo. Professores especializados vão até sua casa ou condomínio.',
    faq: [
      {
        question: 'Qual idade mínima?',
        answer: 'Atendemos crianças a partir de 2 anos de idade.',
      },
      {
        question: 'Preciso ter espaço grande?',
        answer:
          'Não! Adaptamos as atividades para o espaço disponível em casas ou condomínios.',
      },
      {
        question: 'Meu filho nunca jogou, tem problema?',
        answer:
          'Nenhum problema. Nossa metodologia é lúdica e ideal para iniciantes.',
      },
      {
        question: 'Atendem finais de semana?',
        answer: 'Sim, verifique a disponibilidade de horários.',
      },
      {
        question: 'Quanto custa?',
        answer:
          'Solicite uma cotação via WhatsApp para receber os valores atualizados.',
      },
    ],
    content: (
      <div className="space-y-8">
        <div className="prose prose-slate max-w-none text-slate-700">
          <p className="lead text-lg md:text-xl">
            Se você mora na <strong>Vila Olímpia</strong> e busca uma atividade
            física de qualidade para seu filho sem precisar enfrentar o trânsito
            de São Paulo, o <strong>SuperFut Kids</strong> é a solução ideal.
          </p>
          <p>
            Atendemos com excelência os condomínios de alto padrão da região,
            oferecendo aulas personalizadas no conforto e segurança do seu lar.
            Nossa atuação se estende às proximidades do{' '}
            <strong>Itaim Bibi</strong> e <strong>Brooklin</strong>, garantindo
            flexibilidade e comodidade para sua família.
          </p>
        </div>

        <section>
          <h2 className="text-2xl md:text-3xl font-display font-bold text-super-text mb-4">
            Por que escolher o SuperFut Kids na Vila Olímpia?
          </h2>
          <ul className="grid gap-3 sm:grid-cols-2">
            {[
              'Atendimento exclusivo em condomínios de alto padrão',
              'Professores especializados que vão até você',
              'Metodologia lúdica e adaptada ao espaço',
              'Segurança e comodidade para os pais',
            ].map((item, i) => (
              <li key={i} className="flex items-start gap-2">
                <CheckCircle2 className="w-5 h-5 text-super-green mt-1 flex-shrink-0" />
                <span className="font-medium text-slate-700">{item}</span>
              </li>
            ))}
          </ul>
        </section>

        <section className="bg-slate-50 p-6 rounded-2xl border border-slate-100">
          <h2 className="text-2xl md:text-3xl font-display font-bold text-super-text mb-6">
            Especialistas em cada fase
          </h2>
          <div className="space-y-6">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="w-16 h-16 bg-super-yellow rounded-full flex items-center justify-center font-bold text-super-text text-xl flex-shrink-0">
                2-3
              </div>
              <div>
                <h3 className="text-xl font-bold text-super-text mb-2">
                  Primeiros Passos (2 a 3 anos)
                </h3>
                <p className="text-slate-600">
                  Descoberta e diversão! Foco na coordenação motora grossa,
                  reconhecimento corporal e contato inicial com a bola através
                  de brincadeiras.
                </p>
              </div>
            </div>
            <div className="flex flex-col md:flex-row gap-4">
              <div className="w-16 h-16 bg-super-green rounded-full flex items-center justify-center font-bold text-white text-xl flex-shrink-0">
                4-5
              </div>
              <div>
                <h3 className="text-xl font-bold text-super-text mb-2">
                  Craques da Bola (4 a 5 anos)
                </h3>
                <p className="text-slate-600">
                  Desenvolvimento dos fundamentos básicos: condução, passe e
                  chute. Introdução a regras simples e incentivo à cooperação.
                </p>
              </div>
            </div>
            <div className="flex flex-col md:flex-row gap-4">
              <div className="w-16 h-16 bg-super-blue rounded-full flex items-center justify-center font-bold text-white text-xl flex-shrink-0">
                6-7
              </div>
              <div>
                <h3 className="text-xl font-bold text-super-text mb-2">
                  Campeões (6 a 7 anos)
                </h3>
                <p className="text-slate-600">
                  Refinamento técnico e introdução a jogos mais estruturados.
                  Trabalhamos a tomada de decisão, autoconfiança e espírito de
                  equipe.
                </p>
              </div>
            </div>
          </div>
        </section>

        <section>
          <h2 className="text-2xl md:text-3xl font-display font-bold text-super-text mb-4">
            Benefícios do futebol
          </h2>
          <p className="text-slate-600 mb-6">
            Além de aprender futebol, seu filho desenvolverá habilidades
            essenciais para a vida:
          </p>
          <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-4">
            {[
              'Coordenação motora',
              'Desenvolvimento cognitivo',
              'Habilidades sociais',
              'Saúde física',
              'Autoconfiança',
              'Rotina ativa',
            ].map((benefit, i) => (
              <div
                key={i}
                className="bg-white shadow-sm border border-slate-100 p-4 rounded-xl text-center font-bold text-slate-700 hover:border-super-blue transition-colors"
              >
                {benefit}
              </div>
            ))}
          </div>
        </section>

        <section className="bg-gradient-to-br from-green-50 to-blue-50 p-8 rounded-3xl text-center">
          <h2 className="text-2xl md:text-3xl font-display font-bold text-super-text mb-4">
            Agende uma aula na Vila Olímpia!
          </h2>
          <p className="text-lg text-slate-700 mb-8 max-w-2xl mx-auto">
            Transforme a rotina do seu filho com esporte e diversão. Entre em
            contato pelo WhatsApp e consulte a disponibilidade para o seu
            endereço.
          </p>
          <Button
            size="lg"
            className="bg-[#25D366] hover:bg-[#128C7E] text-white font-bold text-lg px-8 rounded-full shadow-lg hover:scale-105 transition-all w-full sm:w-auto"
            asChild
          >
            <a
              href={whatsappUrlVilaOlimpia}
              target="_blank"
              rel="noopener noreferrer"
            >
              <MessageCircle className="w-6 h-6 mr-2" />
              Falar no WhatsApp
            </a>
          </Button>
        </section>
      </div>
    ),
  },
  {
    slug: 'aula-futebol-infantil-itaim-bibi',
    title: 'Aula de Futebol Infantil no Itaim Bibi - A Domicílio',
    excerpt:
      'Aulas de futebol a domicílio para crianças de 2 a 7 anos no Itaim Bibi, SP. Metodologia lúdica, professores especializados e atendimento em condomínios.',
    date: '16 Fev, 2026',
    author: 'Equipe SuperFut',
    category: 'Local',
    location: 'Itaim Bibi, SP',
    image:
      'https://img.usecurling.com/p/800/400?q=modern%20buildings%20sao%20paulo',
    metaDescription:
      'Aulas de futebol a domicílio para crianças de 2 a 7 anos no Itaim Bibi, SP. Professores especializados vão até seu condomínio.',
    faq: [
      {
        question: 'Atendem em condomínios?',
        answer:
          'Sim! Nosso foco é o atendimento domiciliar em condomínios da região.',
      },
      {
        question: 'Qual a idade atendida?',
        answer:
          'Crianças de 2 a 7 anos, com turmas divididas por faixa etária.',
      },
      {
        question: 'Preciso formar turma?',
        answer:
          'Não necessariamente. Atendemos aulas individuais ou em pequenos grupos.',
      },
      {
        question: 'Os professores levam material?',
        answer:
          'Sim, levamos todo o material necessário para a aula (bolas, cones, traves).',
      },
      {
        question: 'Como agendar?',
        answer:
          'Basta entrar em contato pelo WhatsApp e verificar a disponibilidade.',
      },
    ],
    content: (
      <div className="space-y-8">
        <div className="prose prose-slate max-w-none text-slate-700">
          <p className="lead text-lg md:text-xl">
            Morar no <strong>Itaim Bibi</strong> é sinônimo de viver em uma das
            regiões mais nobres e dinâmicas de São Paulo. Para facilitar a
            rotina das famílias, o <strong>SuperFut Kids</strong> leva aulas de
            futebol personalizadas diretamente ao seu condomínio.
          </p>
          <p>
            Sabemos que o tempo é precioso e o trânsito pode ser um obstáculo.
            Por isso, nossa equipe de especialistas atende os principais
            edifícios residenciais da região, garantindo que seu filho pratique
            esporte com segurança e comodidade, próximo a áreas como a{' '}
            <strong>Vila Olímpia</strong> e <strong>Jardins</strong>.
          </p>
        </div>

        <section>
          <h2 className="text-2xl md:text-3xl font-display font-bold text-super-text mb-4">
            Diferenciais no Itaim Bibi
          </h2>
          <ul className="grid gap-3 sm:grid-cols-2">
            {[
              'Atendimento VIP em condomínios de alto padrão',
              'Professores bilíngues (consulte disponibilidade)',
              'Flexibilidade de horários para famílias ocupadas',
              'Segurança total sem sair de casa',
            ].map((item, i) => (
              <li key={i} className="flex items-start gap-2">
                <CheckCircle2 className="w-5 h-5 text-super-green mt-1 flex-shrink-0" />
                <span className="font-medium text-slate-700">{item}</span>
              </li>
            ))}
          </ul>
        </section>

        <section className="bg-slate-50 p-6 rounded-2xl border border-slate-100">
          <h2 className="text-2xl md:text-3xl font-display font-bold text-super-text mb-6">
            Metodologia por Idade
          </h2>
          <div className="space-y-6">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="w-16 h-16 bg-super-yellow rounded-full flex items-center justify-center font-bold text-super-text text-xl flex-shrink-0">
                2-3
              </div>
              <div>
                <h3 className="text-xl font-bold text-super-text mb-2">
                  Iniciação (2-3 anos)
                </h3>
                <p className="text-slate-600">
                  Atividades sensoriais e motoras que estimulam o gosto pelo
                  esporte de forma divertida.
                </p>
              </div>
            </div>
            <div className="flex flex-col md:flex-row gap-4">
              <div className="w-16 h-16 bg-super-green rounded-full flex items-center justify-center font-bold text-white text-xl flex-shrink-0">
                4-5
              </div>
              <div>
                <h3 className="text-xl font-bold text-super-text mb-2">
                  Fundamentos (4-5 anos)
                </h3>
                <p className="text-slate-600">
                  Aprendizado das técnicas básicas como chute e condução, com
                  foco na socialização.
                </p>
              </div>
            </div>
            <div className="flex flex-col md:flex-row gap-4">
              <div className="w-16 h-16 bg-super-blue rounded-full flex items-center justify-center font-bold text-white text-xl flex-shrink-0">
                6-7
              </div>
              <div>
                <h3 className="text-xl font-bold text-super-text mb-2">
                  Jogo (6-7 anos)
                </h3>
                <p className="text-slate-600">
                  Introdução às regras e dinâmica de jogo, desenvolvendo o
                  trabalho em equipe.
                </p>
              </div>
            </div>
          </div>
        </section>

        <section className="bg-gradient-to-br from-green-50 to-blue-50 p-8 rounded-3xl text-center">
          <h2 className="text-2xl md:text-3xl font-display font-bold text-super-text mb-4">
            Leve o SuperFut para o Itaim!
          </h2>
          <p className="text-lg text-slate-700 mb-8 max-w-2xl mx-auto">
            Ofereça o melhor do esporte para seu filho sem sair do condomínio.
            Agende uma aula experimental agora mesmo.
          </p>
          <Button
            size="lg"
            className="bg-[#25D366] hover:bg-[#128C7E] text-white font-bold text-lg px-8 rounded-full shadow-lg hover:scale-105 transition-all w-full sm:w-auto"
            asChild
          >
            <a
              href={whatsappUrlItaim}
              target="_blank"
              rel="noopener noreferrer"
            >
              <MessageCircle className="w-6 h-6 mr-2" />
              Falar no WhatsApp
            </a>
          </Button>
        </section>
      </div>
    ),
  },
  {
    slug: 'aula-futebol-infantil-pinheiros',
    title: 'Aula de Futebol Infantil em Pinheiros - A Domicílio',
    excerpt:
      'Aulas de futebol a domicílio para crianças de 2 a 7 anos em Pinheiros, SP. Metodologia lúdica, uso de parques e áreas comuns.',
    date: '16 Fev, 2026',
    author: 'Equipe SuperFut',
    category: 'Local',
    location: 'Pinheiros, SP',
    image: 'https://img.usecurling.com/p/800/400?q=sunset%20park%20city',
    metaDescription:
      'Aulas de futebol a domicílio para crianças de 2 a 7 anos em Pinheiros, SP. Metodologia lúdica e professores especializados.',
    faq: [
      {
        question: 'Podem ser aulas em parques?',
        answer:
          'Sim! Realizamos aulas na Praça Pôr do Sol e outros locais ao ar livre.',
      },
      {
        question: 'Qual a duração da aula?',
        answer: 'As aulas têm duração média de 50 minutos.',
      },
      {
        question: 'Aceitam grupos de amigos?',
        answer:
          'Com certeza! É uma ótima forma de socialização para as crianças.',
      },
      {
        question: 'Como funciona em dias de chuva?',
        answer:
          'Adaptamos para áreas cobertas ou reagendamos conforme disponibilidade.',
      },
      {
        question: 'Qual o valor?',
        answer: 'Consulte nossa tabela atualizada via WhatsApp.',
      },
    ],
    content: (
      <div className="space-y-8">
        <div className="prose prose-slate max-w-none text-slate-700">
          <p className="lead text-lg md:text-xl">
            Viver em <strong>Pinheiros</strong> é aproveitar o melhor da cultura
            e lazer de São Paulo. O <strong>SuperFut Kids</strong> integra essa
            atmosfera, oferecendo aulas de futebol lúdicas e educativas para
            crianças.
          </p>
          <p>
            Aproveitamos a excelente infraestrutura do bairro, realizando
            atividades tanto em condomínios quanto em locais icônicos como a{' '}
            <strong>Praça Pôr do Sol</strong>. Estamos próximos também da{' '}
            <strong>Vila Madalena</strong> e <strong>Alto de Pinheiros</strong>,
            atendendo toda a região com agilidade e carinho.
          </p>
        </div>

        <section>
          <h2 className="text-2xl md:text-3xl font-display font-bold text-super-text mb-4">
            Vantagens em Pinheiros
          </h2>
          <ul className="grid gap-3 sm:grid-cols-2">
            {[
              'Aulas ao ar livre ou em condomínios',
              'Integração com a natureza e o bairro',
              'Metodologia que estimula a criatividade',
              'Professores experientes e atenciosos',
            ].map((item, i) => (
              <li key={i} className="flex items-start gap-2">
                <CheckCircle2 className="w-5 h-5 text-super-green mt-1 flex-shrink-0" />
                <span className="font-medium text-slate-700">{item}</span>
              </li>
            ))}
          </ul>
        </section>

        <section className="bg-slate-50 p-6 rounded-2xl border border-slate-100">
          <h2 className="text-2xl md:text-3xl font-display font-bold text-super-text mb-6">
            Desenvolvimento Completo
          </h2>
          <div className="space-y-6">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="w-16 h-16 bg-super-yellow rounded-full flex items-center justify-center font-bold text-super-text text-xl flex-shrink-0">
                2-3
              </div>
              <div>
                <h3 className="text-xl font-bold text-super-text mb-2">
                  Primeiros Passos
                </h3>
                <p className="text-slate-600">
                  Exercícios lúdicos que desenvolvem a coordenação e o
                  equilíbrio.
                </p>
              </div>
            </div>
            <div className="flex flex-col md:flex-row gap-4">
              <div className="w-16 h-16 bg-super-green rounded-full flex items-center justify-center font-bold text-white text-xl flex-shrink-0">
                4-5
              </div>
              <div>
                <h3 className="text-xl font-bold text-super-text mb-2">
                  Pequenos Craques
                </h3>
                <p className="text-slate-600">
                  Introdução aos movimentos do futebol e jogos cooperativos.
                </p>
              </div>
            </div>
            <div className="flex flex-col md:flex-row gap-4">
              <div className="w-16 h-16 bg-super-blue rounded-full flex items-center justify-center font-bold text-white text-xl flex-shrink-0">
                6-7
              </div>
              <div>
                <h3 className="text-xl font-bold text-super-text mb-2">
                  Futuros Atletas
                </h3>
                <p className="text-slate-600">
                  Aprimoramento técnico e tático respeitando a fase da criança.
                </p>
              </div>
            </div>
          </div>
        </section>

        <section className="bg-gradient-to-br from-green-50 to-blue-50 p-8 rounded-3xl text-center">
          <h2 className="text-2xl md:text-3xl font-display font-bold text-super-text mb-4">
            Agende sua aula em Pinheiros!
          </h2>
          <p className="text-lg text-slate-700 mb-8 max-w-2xl mx-auto">
            Proporcione saúde e diversão para seu filho. Entre em contato e
            saiba mais sobre nossos horários.
          </p>
          <Button
            size="lg"
            className="bg-[#25D366] hover:bg-[#128C7E] text-white font-bold text-lg px-8 rounded-full shadow-lg hover:scale-105 transition-all w-full sm:w-auto"
            asChild
          >
            <a
              href={whatsappUrlPinheiros}
              target="_blank"
              rel="noopener noreferrer"
            >
              <MessageCircle className="w-6 h-6 mr-2" />
              Falar no WhatsApp
            </a>
          </Button>
        </section>
      </div>
    ),
  },
  {
    slug: 'aula-futebol-infantil-alto-de-pinheiros',
    title: 'Aula de Futebol Infantil no Alto de Pinheiros - A Domicílio',
    excerpt:
      'Aulas de futebol a domicílio no Alto de Pinheiros. Ideal para casas com quintal, condomínios e proximidades do Parque Villa-Lobos.',
    date: '16 Fev, 2026',
    author: 'Equipe SuperFut',
    category: 'Local',
    location: 'Alto de Pinheiros, SP',
    image: 'https://img.usecurling.com/p/800/400?q=house%20garden%20trees',
    metaDescription:
      'Aulas de futebol a domicílio para crianças de 2 a 7 anos no Alto de Pinheiros, SP. Professores vão até sua casa.',
    faq: [
      {
        question: 'Vão até casas?',
        answer:
          'Sim, somos especialistas em atendimento domiciliar, ideal para quem tem quintal.',
      },
      {
        question: 'Qual o espaço necessário?',
        answer:
          'Adaptamos a aula a partir de pequenos espaços, como garagens ou gramados.',
      },
      {
        question: 'Atendem perto do Villa-Lobos?',
        answer:
          'Sim, atendemos toda a região do Alto de Pinheiros e arredores.',
      },
      {
        question: 'É seguro?',
        answer:
          'Totalmente. Nossos professores são verificados e treinados para lidar com crianças.',
      },
      {
        question: 'Tem aula experimental?',
        answer: 'Sim, agende uma aula para conhecer nossa metodologia.',
      },
    ],
    content: (
      <div className="space-y-8">
        <div className="prose prose-slate max-w-none text-slate-700">
          <p className="lead text-lg md:text-xl">
            O <strong>Alto de Pinheiros</strong> é conhecido por sua
            tranquilidade, ruas arborizadas e casas espaçosas. O{' '}
            <strong>SuperFut Kids</strong> aproveita esse cenário perfeito para
            oferecer aulas de futebol exclusivas no conforto do seu lar.
          </p>
          <p>
            Nossa proposta é ideal para famílias que possuem casas com quintal
            ou residem em condomínios próximos ao{' '}
            <strong>Parque Villa-Lobos</strong>. Levamos toda a estrutura
            necessária para transformar seu espaço em um campo de aprendizado e
            diversão, mantendo a natureza residencial e tranquila do bairro.
          </p>
        </div>

        <section>
          <h2 className="text-2xl md:text-3xl font-display font-bold text-super-text mb-4">
            Por que no Alto de Pinheiros?
          </h2>
          <ul className="grid gap-3 sm:grid-cols-2">
            {[
              'Conveniência de aulas no próprio quintal',
              'Aproveitamento de espaços verdes',
              'Foco total na segurança da criança',
              'Profissionais qualificados e de confiança',
            ].map((item, i) => (
              <li key={i} className="flex items-start gap-2">
                <CheckCircle2 className="w-5 h-5 text-super-green mt-1 flex-shrink-0" />
                <span className="font-medium text-slate-700">{item}</span>
              </li>
            ))}
          </ul>
        </section>

        <section className="bg-slate-50 p-6 rounded-2xl border border-slate-100">
          <h2 className="text-2xl md:text-3xl font-display font-bold text-super-text mb-6">
            Nossas Fases
          </h2>
          <div className="space-y-6">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="w-16 h-16 bg-super-yellow rounded-full flex items-center justify-center font-bold text-super-text text-xl flex-shrink-0">
                2-3
              </div>
              <div>
                <h3 className="text-xl font-bold text-super-text mb-2">
                  Exploradores
                </h3>
                <p className="text-slate-600">
                  Descobrindo o corpo e a bola com muita brincadeira.
                </p>
              </div>
            </div>
            <div className="flex flex-col md:flex-row gap-4">
              <div className="w-16 h-16 bg-super-green rounded-full flex items-center justify-center font-bold text-white text-xl flex-shrink-0">
                4-5
              </div>
              <div>
                <h3 className="text-xl font-bold text-super-text mb-2">
                  Jogadores
                </h3>
                <p className="text-slate-600">
                  Desenvolvendo habilidades motoras e primeiros fundamentos.
                </p>
              </div>
            </div>
            <div className="flex flex-col md:flex-row gap-4">
              <div className="w-16 h-16 bg-super-blue rounded-full flex items-center justify-center font-bold text-white text-xl flex-shrink-0">
                6-7
              </div>
              <div>
                <h3 className="text-xl font-bold text-super-text mb-2">
                  Campeões
                </h3>
                <p className="text-slate-600">
                  Desafios técnicos e jogos para estimular o raciocínio.
                </p>
              </div>
            </div>
          </div>
        </section>

        <section className="bg-gradient-to-br from-green-50 to-blue-50 p-8 rounded-3xl text-center">
          <h2 className="text-2xl md:text-3xl font-display font-bold text-super-text mb-4">
            Agende no Alto de Pinheiros!
          </h2>
          <p className="text-lg text-slate-700 mb-8 max-w-2xl mx-auto">
            Transforme o quintal da sua casa em um espaço de desenvolvimento.
            Fale conosco pelo WhatsApp.
          </p>
          <Button
            size="lg"
            className="bg-[#25D366] hover:bg-[#128C7E] text-white font-bold text-lg px-8 rounded-full shadow-lg hover:scale-105 transition-all w-full sm:w-auto"
            asChild
          >
            <a
              href={whatsappUrlAltoPinheiros}
              target="_blank"
              rel="noopener noreferrer"
            >
              <MessageCircle className="w-6 h-6 mr-2" />
              Falar no WhatsApp
            </a>
          </Button>
        </section>
      </div>
    ),
  },
  postFutebol2Anos,
  postBeneficios,
  postAulaDomicilio,
  postFutebolTimidez,
  postCriancaEnergia,
]
